/**
 * Settings → Job Documents & Permits (JOB-DOCS-02 + PERMIT-01, Seth
 * 2026-09-24). Two small lists the office owns:
 *
 *   · Document types — what a job file can be (Plans, Load Calc, Scope…) and
 *     whether the crew's phone shows that type by default. Office Only keeps
 *     contracts and builder POs (prices) off the phone. Any single file can
 *     still be flipped with the eye on the job.
 *   · Permit types — the picker on a job's Permits tab, and which ones show
 *     as one-click "Installs usually need" chips.
 *
 * Stored in org_setting (job_doc_types, permit_types); admins edit.
 */
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Check, Eye, EyeOff, Loader2, Lock, Plus, RotateCcw, X } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { InfoTip } from "@/components/InfoTip";
import { cn } from "@/lib/cn";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { useSaveOrgSetting } from "@/lib/orgSettings";
import { useJobDocTypes, JOB_DOC_TYPES_DEFAULT, JOB_DOC_TYPES_LOCKED, type JobDocType } from "@/features/files/filesCore";
import { usePermitTypes, PERMIT_TYPES_DEFAULT, type PermitType } from "@/features/jobs/PermitSection";

const inputCls = "w-full rounded-md border border-hairline bg-white px-2.5 py-1.5 text-sm text-slate-800 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30";

function slug(label: string, taken: Set<string>): string {
  const base = label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 32) || "type";
  let k = base;
  let n = 2;
  while (taken.has(k)) k = `${base}_${n++}`;
  return k;
}
function move<T>(list: T[], i: number, d: -1 | 1): T[] {
  const j = i + d;
  if (j < 0 || j >= list.length) return list;
  const out = list.slice();
  [out[i], out[j]] = [out[j], out[i]];
  return out;
}

export function JobDocsPermitsAdmin() {
  return (
    <div className="space-y-4">
      <DocTypesCard />
      <PermitTypesCard />
    </div>
  );
}

function DocTypesCard() {
  const canEdit = useCan("settings.manage");
  const { data, isLoading } = useJobDocTypes();
  const save = useSaveOrgSetting();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<JobDocType[] | null>(null);
  const [newLabel, setNewLabel] = useState("");
  const list = draft ?? data ?? JOB_DOC_TYPES_DEFAULT;
  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(data ?? JOB_DOC_TYPES_DEFAULT);
  const set = (i: number, patch: Partial<JobDocType>) => setDraft(list.map((t, k) => (k === i ? { ...t, ...patch } : t)));

  return (
    <Card className="p-5">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
        Document Types
        <InfoTip label="About document types">
          The AI sorts new uploads into these types, and anything showing prices goes to Office Only. The crew&apos;s phone
          shows every type with the eye open; on the job, the office can change a file&apos;s type or flip its eye.
        </InfoTip>
      </h3>
      {isLoading ? (
        <div className="mt-4 h-24 animate-pulse rounded-lg bg-slate-100" />
      ) : (
        <ul className="mt-4 divide-y divide-slate-100 rounded-lg border border-hairline">
          {list.map((t, i) => {
            const locked = JOB_DOC_TYPES_LOCKED.has(t.key);
            return (
              <li key={t.key} className="flex items-center gap-2 px-3 py-2">
                <div className="flex flex-col">
                  <button type="button" disabled={!canEdit || i === 0} onClick={() => setDraft(move(list, i, -1))} className="text-slate-300 hover:text-slate-600 disabled:opacity-30" aria-label="Move up"><ArrowUp className="h-3 w-3" /></button>
                  <button type="button" disabled={!canEdit || i === list.length - 1} onClick={() => setDraft(move(list, i, 1))} className="text-slate-300 hover:text-slate-600 disabled:opacity-30" aria-label="Move down"><ArrowDown className="h-3 w-3" /></button>
                </div>
                <input value={t.label} disabled={!canEdit} onChange={(e) => set(i, { label: e.target.value })} className={cn(inputCls, "max-w-xs")} aria-label="Type name" />
                <button
                  type="button"
                  disabled={!canEdit || t.key === "office"}
                  onClick={() => set(i, { crew: !t.crew })}
                  title={t.key === "office" ? "Office Only never shows on the phone" : t.crew ? "The crew sees this type — click to make it office only" : "Office only — click to let the crew see it"}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold transition-colors disabled:cursor-not-allowed",
                    t.crew ? "bg-brand-50 text-brand-700 hover:bg-brand-100" : "bg-slate-100 text-slate-500 hover:bg-slate-200",
                  )}
                >
                  {t.crew ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                  {t.crew ? "Crew sees it" : "Office only"}
                </button>
                <span className="ml-auto" />
                {locked ? (
                  <span title="Built in — other screens file into this type" className="p-1 text-slate-300"><Lock className="h-3.5 w-3.5" /></span>
                ) : canEdit ? (
                  <button type="button" onClick={() => setDraft(list.filter((_, k) => k !== i))} title="Remove this type (files keep their tag and read as Office Only)" className="rounded p-1 text-slate-300 hover:bg-rose-50 hover:text-rose-500"><X className="h-3.5 w-3.5" /></button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {canEdit && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <form className="flex items-center gap-2" onSubmit={(e) => {
            e.preventDefault();
            const label = newLabel.trim();
            if (!label) return;
            setDraft([...list, { key: slug(label, new Set(list.map((t) => t.key))), label, crew: true }]);
            setNewLabel("");
          }}>
            <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Add a type — e.g. Duct Layout" className={cn(inputCls, "w-64")} />
            <button type="submit" disabled={!newLabel.trim()} className="inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-2.5 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"><Plus className="h-4 w-4" /> Add</button>
          </form>
          <span className="ml-auto flex items-center gap-2">
            {dirty && <button type="button" onClick={() => setDraft(data ?? JOB_DOC_TYPES_DEFAULT)} className="inline-flex items-center gap-1 text-xs font-medium text-slate-400 hover:text-slate-600"><RotateCcw className="h-3 w-3" /> Undo changes</button>}
            <button type="button" disabled={!dirty || save.isPending || list.some((t) => !t.label.trim())}
              onClick={() => {
                const value = list.map((t) => ({ ...t, label: t.label.trim() }));
                // Seed the cache with what we saved so the list never flashes back.
                save.mutate({ key: "job_doc_types", value }, { onSuccess: () => { qc.setQueryData(["org_setting", "job_doc_types"], value); setDraft(null); } });
              }}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-800 disabled:opacity-50">
              {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save Document Types
            </button>
          </span>
        </div>
      )}
      {save.isError && <p className="mt-2 text-xs font-medium text-rose-600">Couldn&apos;t save — only an admin can change this list.</p>}
    </Card>
  );
}

function PermitTypesCard() {
  const canEdit = useCan("settings.manage");
  const current = usePermitTypes();
  const save = useSaveOrgSetting();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<PermitType[] | null>(null);
  const [newLabel, setNewLabel] = useState("");
  const list = draft ?? current;
  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(current);
  const set = (i: number, patch: Partial<PermitType>) => setDraft(list.map((t, k) => (k === i ? { ...t, ...patch } : t)));

  return (
    <Card className="p-5">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
        Permit Types
        <InfoTip label="About permit types">
          The permit picker on every job. Types marked &ldquo;Installs usually need it&rdquo; show as one-click buttons on an
          install&apos;s Permits tab. Cities live under Permit Cities.
        </InfoTip>
      </h3>
      <ul className="mt-4 divide-y divide-slate-100 rounded-lg border border-hairline">
        {list.map((t, i) => (
          <li key={`${i}-${t.label}`} className="flex items-center gap-2 px-3 py-2">
            <div className="flex flex-col">
              <button type="button" disabled={!canEdit || i === 0} onClick={() => setDraft(move(list, i, -1))} className="text-slate-300 hover:text-slate-600 disabled:opacity-30" aria-label="Move up"><ArrowUp className="h-3 w-3" /></button>
              <button type="button" disabled={!canEdit || i === list.length - 1} onClick={() => setDraft(move(list, i, 1))} className="text-slate-300 hover:text-slate-600 disabled:opacity-30" aria-label="Move down"><ArrowDown className="h-3 w-3" /></button>
            </div>
            <input value={t.label} disabled={!canEdit} onChange={(e) => set(i, { label: e.target.value })} className={cn(inputCls, "max-w-xs")} aria-label="Permit type" />
            <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-slate-600">
              <input type="checkbox" checked={t.suggest} disabled={!canEdit} onChange={(e) => set(i, { suggest: e.target.checked })} className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600" />
              Installs usually need it
            </label>
            <span className="ml-auto" />
            {canEdit && list.length > 1 && (
              <button type="button" onClick={() => setDraft(list.filter((_, k) => k !== i))} title="Remove" className="rounded p-1 text-slate-300 hover:bg-rose-50 hover:text-rose-500"><X className="h-3.5 w-3.5" /></button>
            )}
          </li>
        ))}
      </ul>
      {canEdit && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <form className="flex items-center gap-2" onSubmit={(e) => {
            e.preventDefault();
            const label = newLabel.trim();
            if (!label || list.some((t) => t.label.toLowerCase() === label.toLowerCase())) return;
            setDraft([...list, { label, suggest: false }]);
            setNewLabel("");
          }}>
            <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Add a type — e.g. Fireplace" className={cn(inputCls, "w-64")} />
            <button type="submit" disabled={!newLabel.trim()} className="inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-2.5 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"><Plus className="h-4 w-4" /> Add</button>
          </form>
          <span className="ml-auto flex items-center gap-2">
            {dirty && <button type="button" onClick={() => setDraft(null)} className="inline-flex items-center gap-1 text-xs font-medium text-slate-400 hover:text-slate-600"><RotateCcw className="h-3 w-3" /> Undo changes</button>}
            <button type="button" disabled={!dirty || save.isPending || list.some((t) => !t.label.trim())}
              onClick={() => {
                const value = list.map((t) => ({ ...t, label: t.label.trim() }));
                save.mutate({ key: "permit_types", value }, { onSuccess: () => { qc.setQueryData(["org_setting", "permit_types"], value); setDraft(null); } });
              }}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-800 disabled:opacity-50">
              {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save Permit Types
            </button>
          </span>
        </div>
      )}
      {save.isError && <p className="mt-2 text-xs font-medium text-rose-600">Couldn&apos;t save — only an admin can change this list.</p>}
      {JSON.stringify(current) === JSON.stringify(PERMIT_TYPES_DEFAULT) && <p className="mt-2 text-[11px] text-slate-400">Showing the starter list.</p>}
    </Card>
  );
}
